import { Arg, Ctx, Mutation, Resolver, Directive } from 'type-graphql'
import { In } from 'typeorm'
import type { FileUpload } from 'graphql-upload/GraphQLUpload.js'
import GraphQLUpload from 'graphql-upload/GraphQLUpload.js'
import { Domain, getRedirectSubdomainPath, getRepository } from '@things-factory/shell'

import { Group } from '../group/group.js'
import { Board } from './board.js'
import { BoardHistory } from './board-history.js'
import { BoardPatch, NewBoard } from './board-type.js'
import { scheduleThumbnail } from './thumbnail-scheduler.js'

/**
 * prev와 next 사이에서 자릿수가 최소인 중간값을 찾는다.
 * (prev, next) open interval에서 가장 깔끔한 숫자를 선택.
 */
function optimalMidValue(prev: number, next: number): number {
  if (prev >= next) return prev - 1

  const mid = (prev + next) / 2
  for (let p = 0; p <= 10; p++) {
    const scale = 10 ** p
    const low = Math.floor(prev * scale) + 1
    const high = Math.ceil(next * scale) - 1
    if (low <= high) {
      return Math.min(Math.max(Math.round(mid * scale), low), high) / scale
    }
  }
  return mid
}

async function parseJSONFile(uploadedFile: FileUpload): Promise<any> {
  var { createReadStream } = await uploadedFile

  return new Promise((resolve, reject) => {
    const chunks: Uint8Array[] = []

    createReadStream()
      .on('data', (chunk: Uint8Array) => {
        chunks.push(chunk)
      })
      .on('end', () => {
        try {
          const fileContents = Buffer.concat(chunks).toString('utf-8')
          const jsonData = JSON.parse(fileContents)
          resolve(jsonData)
        } catch (error) {
          reject(error)
        }
      })
      .on('error', (error: Error) => {
        reject(error)
      })
  })
}

@Resolver(Board)
export class BoardMutation {
  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Board, { description: 'Creates a new board.' })
  async createBoard(@Arg('board') board: NewBoard, @Ctx() context: ResolverContext): Promise<Board> {
    const { domain, user, notify, tx } = context.state
    const repository = getRepository(Board, tx)
    const groupRepository = getRepository(Group, tx)

    const oldBoard: Board = await repository.findOneBy({
      name: board.name,
      domain: { id: domain.id }
    })

    if (oldBoard) {
      throw new Error(context.t('error.board name is already taken', { name: board.name }))
    }

    const newBoard: Board = {
      ...board
    }

    newBoard.thumbnail ||=
      'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==' /* empty thumbnail */

    if (board.groupId) {
      newBoard.group = await groupRepository.findOneBy({
        id: board.groupId
      })
    }

    // 새 보드는 맨 앞에 배치
    const { minSortOrder } = (await repository
      .createQueryBuilder('board')
      .select('MIN(board.sortOrder)', 'minSortOrder')
      .where('board.domain = :domainId', { domainId: domain.id })
      .getRawOne()) || { minSortOrder: null }

    const created = await repository.save({
      domain,
      ...newBoard,
      sortOrder: minSortOrder != null ? minSortOrder - 1 : 0,
      state: 'draft',
      creator: user,
      updater: user
    })

    // 새 보드의 thumbnail 도 백그라운드로 생성 — mutation 응답 즉시 반환.
    // 클라이언트가 thumbnail 을 보낸 경우 (NewBoard.thumbnail 있음) 는 그것을 사용
    // 하고 puppeteer 생략. 그게 빈 gif 인 경우만 실제 캡처.
    if (newBoard.model && (!board.thumbnail || board.thumbnail.startsWith('data:image/gif'))) {
      scheduleThumbnail(created.id, newBoard.model, context)
    }

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${created.name}' created`,
        body: `Board '${created.name}' created by ${user.name}\n${created.description}`,
        url: getRedirectSubdomainPath(context, domain, `/board-viewer/${created.id}`)
      })

    return created
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Board, {
    description: 'Clones a board from an existing one, potentially into a different domain.'
  })
  async cloneBoard(
    @Arg('id') id: string,
    @Arg('patch') patch: BoardPatch,
    @Arg('targetSubdomain') targetSubdomain: string,
    @Arg('targetGroupId', { nullable: true }) targetGroupId: string,
    @Ctx() context: ResolverContext
  ): Promise<Board> {
    const { domain, user, notify, tx } = context.state
    const { t } = context
    const repository = getRepository(Board, tx)

    const board = await repository.findOneBy({ domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, id })

    if (!patch.name || (patch.name == board.name && targetSubdomain == domain.subdomain)) {
      throw new Error(t('error.name must be unique from the original board', { name: patch.name }))
    }

    if (targetSubdomain != domain.subdomain) {
      if ((await repository.count({ where: { domain: { subdomain: targetSubdomain }, name: patch.name } })) > 0) {
        throw new Error(t('error.name must be unique from the original board', { name: patch.name }))
      }
    }

    var targetDomain = domain
    if (targetDomain && domain.subdomain != targetSubdomain) {
      targetDomain = await getRepository(Domain, tx).findOneBy({ subdomain: targetSubdomain })
      if (!targetDomain) {
        throw new Error(`given subdomain(${targetSubdomain}) not found`)
      }
    }

    var targetGroup = null
    if (targetGroupId) {
      targetGroup = await getRepository(Group, tx).findOneBy({ domain: { id: targetDomain.id }, id: targetGroupId })
      if (!targetGroup) {
        throw new Error(`given group(${targetGroupId}) in domain(${targetSubdomain}) not found`)
      }
    }

    const { id: excluded, ...clone } = board

    const cloned = await repository.save({
      domain: targetDomain,
      ...clone,
      ...patch,
      group: targetGroup,
      version: 0,
      state: 'draft',
      updater: user,
      creator: user
    })

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${cloned.name}' cloned`,
        body: `Board '${cloned.name}' cloned by ${user.name}\n${cloned.description}`,
        image: getRedirectSubdomainPath(context, targetDomain, `/thumbnail/${cloned.id}`),
        url: getRedirectSubdomainPath(context, targetDomain, `/board-viewer/${cloned.id}`)
      })

    return cloned
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Board, {
    description: 'Updates a board. If a model is provided, it also generates a new thumbnail.'
  })
  async updateBoard(
    @Arg('id') id: string,
    @Arg('patch') patch: BoardPatch,
    @Ctx() context: ResolverContext
  ): Promise<Board> {
    const { domain, user, notify, tx } = context.state
    const repository = getRepository(Board, tx)

    const board = await repository.findOne({
      where: { domain: { id: domain.id }, id },
      relations: ['creator']
    })

    if (!board) {
      throw new Error('board not found')
    }

    if (patch.model) {
      // 데이터 무결성 — model JSON 의 syntax 검증. invalid JSON 영속 차단.
      // (의미 검증 [schema] 은 별도 layer — board-ai 의 tool 단계에서 수행.)
      try {
        const parsed = JSON.parse(patch.model)
        if (!parsed || typeof parsed !== 'object') {
          throw new Error('Board model must be a JSON object')
        }
        if (typeof parsed.width !== 'number' || typeof parsed.height !== 'number') {
          throw new Error('Board model must have numeric width and height')
        }
        if (parsed.components !== undefined && !Array.isArray(parsed.components)) {
          throw new Error('Board model components must be an array if present')
        }
      } catch (e: any) {
        // 명시 에러 — silent 데이터 손상보다 명확한 reject 선호
        throw new Error(`Invalid board model: ${e.message}`)
      }

      // Thumbnail 생성은 fire-and-forget 백그라운드. mutation 응답이 puppeteer 완료
      // (또는 풀 timeout) 에 차단되지 않도록 분리. 빈번한 저장 시 per-board debounce
      // + model hash dedup 으로 puppeteer 호출 횟수 자체를 줄인다. 자세한 동작은
      // ./thumbnail-scheduler.ts 참고.
      //
      // patch.thumbnail 은 set 하지 않는다 — 클라이언트가 thumbnail 을 만들 수 없으므로
      // (권한 분리 / 렌더 결정론 / 무인 트리거 / ZPL 정확성), 백엔드만 생성한다. 새
      // thumbnail 이 만들어지기 전까지는 board.thumbnail 의 기존 값이 유지된다.
      scheduleThumbnail(id, patch.model, context)
    }

    const { groupId, ...patched } = patch

    if (groupId !== undefined) {
      const groupRepository = getRepository(Group, tx)
      board.group = groupId
        ? (await groupRepository.findOneBy({
            domain: { id: domain.id },
            id: groupId
          })) || null
        : null
    }

    const updated = await repository.save({
      domain,
      ...board,
      ...patched,
      state: 'draft',
      updater: user
    })

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${updated.name}' updated`,
        body: `Board '${updated.name}' updated by ${user.name}\n${updated.description}`,
        image: getRedirectSubdomainPath(context, domain, `/thumbnail/${updated.id}`),
        url: getRedirectSubdomainPath(context, domain, `/board-viewer/${updated.id}`)
      })

    return updated
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Board, { description: 'Releases a board, making it public and creating a version history.' })
  async releaseBoard(
    @Arg('id') id: string,
    @Ctx() context: ResolverContext,
    @Arg('comment', { nullable: true }) comment?: string
  ): Promise<Board> {
    const { domain, user, notify, tx } = context.state
    const repository = getRepository(Board, tx)
    const historyRepository = getRepository(BoardHistory, tx)

    const board = await repository.findOne({
      where: { domain: { id: domain.id }, id },
      relations: ['creator']
    })

    if (!board) {
      throw new Error(`Board given id(${id}) is not found`)
    }

    if (board.state == 'released') {
      throw new Error(`Board given id(${id}) is already released`)
    }

    // 히스토리에서 max version 가져오기
    const maxHistory = await historyRepository
      .createQueryBuilder('history')
      .select('MAX(history.version)', 'max')
      .where('history.originalId = :id', { id: board.id })
      .getRawOne()

    const nextVersion = maxHistory?.max ? Number(maxHistory.max) + 1 : 1

    const updated = await repository.save({
      domain,
      ...board,
      version: nextVersion,
      state: 'released',
      updater: user,
      // BoardHistoryEntitySubscriber 가 스냅샷 생성 시 history.comment 로 주입(Board 컬럼 아님).
      __releaseComment: comment ?? null
    } as any)

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${updated.name}' released`,
        body: `Board '${updated.name}' released by ${user.name}\n${updated.description}`,
        image: getRedirectSubdomainPath(context, domain, `/thumbnail/${updated.id}`),
        url: getRedirectSubdomainPath(context, domain, `/board-viewer/${updated.id}`)
      })

    return updated
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Board, { description: 'Reverts a board to a specific historical version.' })
  async revertBoardVersion(
    @Arg('id') id: string,
    @Arg('version') version: number,
    @Ctx() context: ResolverContext
  ): Promise<Board> {
    const { domain, user, notify, tx } = context.state
    const repository = getRepository(Board, tx)

    const board = await repository.findOne({
      where: { domain: { id: domain.id }, id },
      relations: ['creator']
    })

    if (!board) {
      throw new Error(`Board with id(${id}) is not found`)
    }

    const historyRepository = getRepository(BoardHistory, tx)

    const boardHistory = await historyRepository.findOne({
      where: { domain: { id: domain.id }, originalId: id, version },
      order: { version: 'DESC' }
    })

    if (!boardHistory) {
      throw new Error(`Board with id:version(${id}:${version}) is not found`)
    }

    const updated = await repository.save({
      domain,
      ...board,
      model: boardHistory.model,
      thumbnail: boardHistory.thumbnail,
      state: 'draft',
      updater: user
    })

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${updated.name}' updated`,
        body: `Board '${updated.name}' updated by ${user.name}\n${updated.description}`,
        image: getRedirectSubdomainPath(context, domain, `/thumbnail/${updated.id}`),
        url: getRedirectSubdomainPath(context, domain, `/board-viewer/${updated.id}`)
      })

    return updated
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Boolean, { description: 'Deletes a board.' })
  async deleteBoard(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, user, notify, tx } = context.state
    const repository = getRepository(Board, tx)
    const board = await repository.findOneBy({ domain: { id: domain.id }, id })

    const deleted = await repository.softDelete(id)

    notify &&
      notify({
        mode: 'in-app',
        title: `Board '${board.name}' deleted`,
        body: `Board '${board.name}' deleted by ${user.name}\n${board.description}`
      })

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => [Board], { description: 'Imports multiple boards from JSON files.' })
  async importBoards(
    @Arg('groupId') groupId: string,
    @Arg('files', () => [GraphQLUpload]) files: FileUpload[],
    @Arg('overwrite') overwrite: boolean,
    @Ctx() context: ResolverContext
  ): Promise<Board[]> {
    const { domain, user, notify, tx } = context.state
    const groupRepository = getRepository(Group, tx)
    const boardRepository = getRepository(Board, tx)
    const group = await groupRepository.findOneBy({ domain: { id: domain.id }, id: groupId })

    if (!group) {
      throw new Error(`Group with id(${groupId}) is not found`)
    }

    const boards = []

    for (const file of files) {
      const { id, name, description, model, thumbnail } = await parseJSONFile(file)

      var sameNameBoard = await boardRepository.findOneBy({ domain: { id: domain.id }, name })
      var sameIdBoard = await boardRepository.findOneBy({ id })

      if (overwrite) {
        var board = {} as any

        if (sameIdBoard) {
          if (overwrite && sameIdBoard.domainId != domain.id) {
            throw new Error(`Board with id(${id}) is already taken in another domain`)
          }

          board = {
            ...sameIdBoard,
            name
          }

          if (sameNameBoard && sameIdBoard.id != sameNameBoard.id) {
            /* 이름 충돌 회피 */
            board.name = `${board.name}(${Date.now()})`
          }
        } else {
          board = {
            id,
            name: sameNameBoard ? `${name}(${Date.now()})` : name,
            version: 0,
            creator: user
          }
        }
      } else {
        board = {
          name,
          version: 0,
          creator: user
        }

        /* ID가 없으면, 사용해도 됨 */
        if (!sameIdBoard) {
          board.id = id
        }

        /* 이름 충돌 회피 */
        if (sameNameBoard) {
          board.name = `${board.name}(${Date.now()})`
        }
      }

      boards.push(
        await boardRepository.save({
          ...board,
          domain,
          description,
          model: typeof model != 'string' ? JSON.stringify(model) : model,
          thumbnail,
          group,
          state: 'draft',
          updater: user
        })
      )
    }

    notify &&
      notify({
        mode: 'in-app',
        title: `${boards.length} Board(s) are imported`,
        body: `${boards.length} Board(s) are imported into group ${group.name} by ${user.name}`
      })

    return boards
  }

  @Mutation(returns => Boolean, { description: 'Reorders a board between two adjacent boards by ID.' })
  @Directive('@privilege(category: "board", privilege: "mutation")')
  @Directive('@transaction')
  async reorderBoard(
    @Arg('id') id: string,
    @Arg('prevId', { nullable: true }) prevId: string,
    @Arg('nextId', { nullable: true }) nextId: string,
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state
    const repo = tx.getRepository(Board)
    const domainId = domain.id

    // prev/next의 sortOrder 조회
    let prevOrder: number | null = null
    let nextOrder: number | null = null

    if (prevId) {
      const prev = await repo.findOne({ where: { id: prevId, domain: { id: domainId } } as any })
      prevOrder = prev?.sortOrder ?? null
    }
    if (nextId) {
      const next = await repo.findOne({ where: { id: nextId, domain: { id: domainId } } as any })
      nextOrder = next?.sortOrder ?? null
    }

    // sortOrder 계산
    let prev = prevOrder ?? (nextOrder != null ? nextOrder - 2 : 0)
    let next = nextOrder ?? prev + 2

    // prev와 next의 간격이 너무 좁으면 전체 보드 sortOrder 정규화
    if (next - prev < 0.001) {
      const allBoards = await repo.find({
        where: { domain: { id: domainId } } as any
      })

      // DB 독립적인 정렬 (NULL → 맨 뒤, sortOrder ASC, name ASC)
      allBoards.sort((a, b) => {
        const sa = a.sortOrder ?? Number.MAX_SAFE_INTEGER
        const sb = b.sortOrder ?? Number.MAX_SAFE_INTEGER
        if (sa !== sb) return sa - sb
        return (a.name || '').localeCompare(b.name || '')
      })

      for (let i = 0; i < allBoards.length; i++) {
        if (allBoards[i].sortOrder !== i) {
          await repo.update({ id: allBoards[i].id! }, { sortOrder: i })
          allBoards[i].sortOrder = i
        }
      }

      // 정규화된 값으로 prev/next 재조회
      if (prevId) {
        prev = allBoards.find(b => b.id === prevId)?.sortOrder ?? prev
      }
      if (nextId) {
        next = allBoards.find(b => b.id === nextId)?.sortOrder ?? next
      } else {
        next = prev + 2
      }
    }

    const sortOrder = optimalMidValue(prev, next)

    await repo.update({ id, domain: { id: domainId } }, { sortOrder })
    return true
  }
}
